import * as React from 'react'; 

import config from '../../config';
import { UserContext } from '../providers/UserProvider';
import { ShopByProductContext } from './ShopByProductProvider';
import { ProductCategoryContext } from './ProductCategoryProvider';
import useDispensaryProximity from '../dispensaries/useDispensaryProximity';
import { useSelector } from 'react-redux';
import { getCategoryInfoItem } from './category-search-utils';
import Helmet from 'react-helmet';
import CategorySearchHeader from './CategorySearchHeader';
import AddressInputSection from '../address/AddressInputSection';
import Spinner from '../common/Spinner';
import ProductFilterCarousel from '../dispensary/ProductFilterCarousel';
import BulkDiscountMsg from '../dispensary/BulkDiscountMsg';
import DispensarySection from './DispensarySection';
import AddedToCartCheck from '../products/AddedToCartCheck';
import NotAvailableMessage from './NotAvailableMessage';
import PropTypes from 'prop-types';

import styles from './CategorySearchResults.module.css';
import PreviewMessage from '../preview/PreviewMessage';

/**
 * Display ~4 products for the selected category for all dispensaries
 * ( Dispensaries ordered by proximity )
 * 
 * @param {string} productCategory - via parent, this is category.name not .display_name
 */

const CategorySearchResults = ({
  productCategory, 
}) => {

  const { setSearchLocation } = React.useContext(UserContext);
  const { dispensariesByDistance, distanceMap, userZipCode, loading:dispensariesLoading } = useDispensaryProximity();
  const { categoryProductsByDispensary, category, setCategory, loading:productsLoading } = React.useContext(ShopByProductContext);
  const { productCategories } = React.useContext(ProductCategoryContext);
  const isPickup = useSelector(state => state.is_pickup); 
 
  const [dataLoaded, setDataLoaded] = React.useState();
  const [categoryDisplayName, setCategoryDisplayName] = React.useState('');
  const [showAddedCheck, setShowAddedCheck] = React.useState(false);
  const filterRef = React.createRef();

  // Called by the product filter carousel
  const updateCurrentCategory = (category) => {
    setCategory(category.name);
    setCategoryDisplayName(category.display_name);
    if (filterRef.current) {
      window.scrollTo(0,0);
      // TODO: Remove? trying this with scroll to top for now
      // filterRef.current.scrollIntoView();
    }
  }

  const stickyFilterStyle = {
    position: 'sticky',
    zIndex: 1, /* zIndex: 2 will stack on top of modals */
    top: '3rem'
  }; 

  // Handle initial load
  React.useEffect(()=> {
    if ( dispensariesLoading ||
         productsLoading ||
         !categoryDisplayName ) { 
      setDataLoaded(false);
    } else if (!dataLoaded) {
      setDataLoaded(true); 
    }
  }, [dispensariesLoading, productsLoading, categoryDisplayName, dataLoaded]);
  
  React.useEffect(() => {
    if (productCategories?.length && !categoryDisplayName) {
      const category = productCategories.find((cat => cat.name === productCategory));   
      setCategoryDisplayName(category.display_name);   
    }
  }, [categoryDisplayName, productCategories, productCategory]); 
  
  return (
    <>
      <Helmet>
        <title>{`Zip Run - Shop by Product: ${categoryDisplayName}`}</title>
      </Helmet>
      { productCategories && categoryDisplayName
        ? <CategorySearchHeader 
            heroImg={getCategoryInfoItem(categoryDisplayName, 'image_url', productCategories)} 
            heroLbl={categoryDisplayName} />
        : null
      }
      { category
        ? <div style={stickyFilterStyle}>
            {/* scrollTo element */}
            <div ref={filterRef} style={{ width:'1px', position:'relative', top:'-42px'}}></div>
            <div className={styles.filterWrap}>
              <ProductFilterCarousel currentCategory={category} handleClick={updateCurrentCategory}/>
            </div>
          </div>
        : null
      }  
      { dataLoaded
        ? <>
            <div className={styles.searchResults}>
              { config.IS_PREVIEW_MODE
                ? <PreviewMessage />
                : categoryDisplayName === 'Flower'
                  ? <BulkDiscountMsg />
                  : <></>
              }    
              { dispensariesByDistance.length
                ? Object.keys(categoryProductsByDispensary).length 
                  ? dispensariesByDistance.map((disp) => {
                      const key = disp.id;
                      return ( categoryProductsByDispensary[key]
                        ? <div key={`results${key}`} className={styles.listingGrid}>
                            <DispensarySection
                              dispensaryId={key}
                              distanceMap={distanceMap}
                              productCategoryName={categoryDisplayName}
                              handleClick={() => setShowAddedCheck(true)}
                              categoryCount={categoryProductsByDispensary[key].length}
                              searchResultProducts={categoryProductsByDispensary[key].slice(0,4)}
                            />
                          </div>
                        : null  
                      )
                    })
                  : <NotAvailableMessage categoryName={categoryDisplayName} zipCode={userZipCode} isPickup={isPickup} />
                : <AddressInputSection onAddressSelect={setSearchLocation} />
              }
              { showAddedCheck && 
                <AddedToCartCheck closeFn={() => { setShowAddedCheck(false) }} />
              }
            </div> 
          </>
        : <Spinner />  
      }
    </>
  );
};

CategorySearchResults.propTypes = {
  productCategory: PropTypes.string.isRequired
}

export default CategorySearchResults;